import React, { useEffect, useState, useCallback } from 'react';
import MuiAddCircleOutline from '@material-ui/icons/AddCircleOutline';
import EditIcon from '@material-ui/icons/Edit';
import { toast } from 'react-toastify';
import history from 'lib/history';
import CostToServeTooltip from 'components/deal-results/general-information/cts-tooltip';
import labels from 'constants/labels';
import messages from 'constants/messages';
import { testIdAddDealButton } from 'pages/compare/_test/test-ids';
import { IDeal, IDealDataSelector } from 'store/deals/types';
import { fromApi } from 'lib/helpers/deal-formatters';
import AddDealToCompareModal from 'pages/compare/compare-modal';
import uuid from 'uuid';
import IconButton from '@material-ui/core/IconButton';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { withStyles } from '@material-ui/core/styles';
import Collapse from '@material-ui/core/Collapse';
import { IFieldOption } from 'store/field-options/types';
import { IDealWithEvaluation } from 'store/deal-comparison/types';
import {
  ComparisonTable,
  IconWrapper,
  EditIconWrap,
  EditText,
  AddDealText,
  DealWrap,
  Blanktd,
  SubHeaderData,
  WeightHeader,
  UnitToggle,
  CostToServeData,
  CostToServeBottom,
  CostToServeTop,
  CreditTermData,
  CreditBottom,
} from 'pages/compare/components';
import { ELoadingType } from 'store/loading/types';
import colors from 'theme/colors';
import { EEvaluationUnit } from 'store/deal-evaluation/types';
import ToolTip from 'components/i-tooltip';
import {
  useEffectHeaderConst,
  costToServeYieldHeaderConst,
  pricingFormulaHeaderConst,
  volumeLabelHeaderConst,
  currencyDollarHeaderConst,
  totalRevenueHeaderConst,
  totalNetbackHeaderConst,
  totalMarginHeaderConst,
  nonstandardRisksHeaderConst,
  totalMarginAtRiskHeaderConst,
  sharpeRatioHeaderConst,
  contractBatna1HeaderConst,
  ContractWOOptionsHeaderConst,
  creditTermHeaderConst,
  optionsValueHeaderConst,
} from 'constants/numericalValues';
import { headersData } from './types';
import RadioButtonsGroup from './RadioButtonsGroup';
import UnitToggles from './unit';
import { updateDealWithAllProduct } from './helpers';
import { RenderDealDetails, RenderDealEditView, RenderShowOptions } from './DefaultViewComponents';

const { primary, disable } = colors;

export const IconButtons = withStyles({
  root: {
    color: primary,
    float: 'right',
  },
})(IconButton);

export const IconDisableButtons = withStyles({
  root: {
    float: 'right',
    cursor: 'not-allowed',
    pointerEvents: 'none',
    color: disable,
  },
})(IconButton);

const notSpecified = 'Not Specified';

const checkProductNameLength = (productName: { name: string; selected: boolean }[] | undefined) => {
  return productName && productName.length > 1;
};

const checkShowOptionsCapCustomerFlexEarlyTerminationAndExtensions = (showOptions: {
  cap: boolean;
  customerFlex: boolean;
  earlyTermination: boolean;
  extensions: boolean;
}) => {
  return !showOptions.cap && !showOptions.customerFlex && !showOptions.earlyTermination && !showOptions.extensions;
};

const checkShowOptionsFloorPriceSwitchPullValAndPushVal = (showOptions: {
  floor: boolean;
  priceSwitch: boolean;
  pullVal: boolean;
  pushVal: boolean;
}) => {
  return !showOptions.floor && !showOptions.priceSwitch && !showOptions.pullVal && !showOptions.pushVal;
};

const checkProductName = (productName: { name: string; selected: boolean }[] | undefined) => {
  return productName?.[0]?.name;
};

const checkOpenCostToserveValue = (openCostToserve: boolean) => {
  return openCostToserve ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />;
};

const checkOpenCreditTermValue = (openCreditTerm: boolean) => {
  return openCreditTerm ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />;
};

const checkOpenOptionValue = (openOption: boolean) => {
  return openOption ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />;
};

const checkOpenRisksValue = (openRisks: boolean) => {
  return openRisks ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />;
};

const checkNonstandardRisksLength = (nonstandardRisks: string[]) => {
  return nonstandardRisks.length === 0 ? 'Not Included' : 'Included';
};

const includeNonstandardRisksHeader = (nonstandardRisks: string[], sbh: string) => {
  return nonstandardRisks.includes(sbh) ? 'Included' : 'Not Included';
};

const getNonstandardRisksHeaderText = (nonstandardRisks: string[]) => {
  return nonstandardRisks === null || nonstandardRisks === undefined
    ? notSpecified
    : checkNonstandardRisksLength(nonstandardRisks);
};

const getNonstandardRisksSubHeaderText = (nonstandardRisks: string[], sbh: string) => {
  return nonstandardRisks === null || nonstandardRisks === undefined
    ? notSpecified
    : includeNonstandardRisksHeader(nonstandardRisks, sbh);
};

export interface ICompareDefaultViewProps {
  addDealsFromDashboard: (deals: IDeal[]) => void;
  availableDeals: IDeal[];
  dealsWithEvaluation: IDealWithEvaluation[];
  evaluateDeals: () => void;
  removeDeal: (index: number) => void;
  shiftDealLeft: (index: number) => void;
  shiftDealRight: (index: number) => void;
  dealsData: IDealDataSelector[];
  selectedUnitType: EEvaluationUnit;
  selectCurrentUnit: string;
  availableProducts: IFieldOption[] | undefined;
  setLoading: (loading: ELoadingType) => void;
  updateDeals: (deal: IDeal) => void;
  compareDealIds: (string | undefined)[];
  setCompareDealIds: (dealIds: (string | undefined)[]) => void;
  removeAllDeals: () => void;
}

/**
 * will take all data ready to display
 */
export default function CompareDefaultView(props: ICompareDefaultViewProps) {
  const {
    addDealsFromDashboard,
    availableDeals,
    dealsWithEvaluation,
    evaluateDeals,
    removeDeal,
    shiftDealLeft,
    shiftDealRight,
    dealsData,
    selectedUnitType,
    selectCurrentUnit,
    availableProducts,
    setLoading,
    updateDeals,
    compareDealIds,
    setCompareDealIds,
    removeAllDeals,
  } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditClick, setIsEditClick] = useState(false);
  const [openCostToserve, setOpenCostToServe] = useState(false);
  const [openCreditTerm, setOpenCreditTerm] = useState(false);
  const [openRisks, setOpenRisks] = useState(false);
  const [openOption, setOpenOption] = useState(false);
  const [showOptions, setShowOptions] = useState({
    cap: true,
    customerFlex: true,
    earlyTermination: true,
    extensions: true,
    priceSwitch: true,
    pushVal: true,
    pullVal: true,
    floor: true,
  });
  const [headers, setHeaders] = useState(headersData);

  const currencyByWeightLabel: string = labels.currency[selectCurrentUnit] ?? '$/T';
  const volumeLabel = selectedUnitType === EEvaluationUnit.LBS ? labels.volume.pound : labels.volume.ton;

  useEffect(() => {
    const urlText: string = window.location.href;
    if (urlText.lastIndexOf('/compare/') !== -1) {
      const urlDealIds: string[] = urlText
        .substring(urlText.lastIndexOf('/') + 1)
        .toString()
        .split(',');
      const dealIds: string[] = [];
      availableDeals.forEach((deal: IDeal) => {
        if (deal.id && urlDealIds.includes(deal.id)) {
          dealIds.push(deal.id);
        }
      });
      if (dealIds.length === 0) {
        toast.warn(messages.warnings.dealNotFound, { autoClose: 5000 });
        return;
      }
      setCompareDealIds(dealIds);
      const filteredDeals: IDeal[] = availableDeals.filter((deal: IDeal) => deal.id && dealIds.includes(deal.id));
      const dealsWithProductName = filteredDeals.map((deal: IDeal) =>
        updateDealWithAllProduct(fromApi(deal), availableProducts)
      );
      addDealsFromDashboard(dealsWithProductName);
      evaluateDeals();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const UniqueNonStandardRisks = useCallback(() => {
    const riskArrs: string[] = [];
    dealsData.forEach((ns: IDealDataSelector) =>
      ns.nonstandardRisks?.map((str: string) => {
        if (!riskArrs.includes(str)) {
          riskArrs.push(str);
        }
        return riskArrs;
      })
    );
    return riskArrs;
  }, [dealsData]);

  useEffect(() => {
    const nonStndardData = UniqueNonStandardRisks();
    if (nonStndardData.length > 0 && headers[useEffectHeaderConst].subHeader) {
      const headersTemp = headers;
      headersTemp[useEffectHeaderConst].subHeader = nonStndardData;
      setHeaders(headersTemp);
    }
  }, [dealsData.length, headers, UniqueNonStandardRisks]);

  useEffect(() => {
    if (dealsData.length > 0) {
      setShowOptions({
        cap: dealsData.some(({ cap }: IDealDataSelector) => cap !== '-'),
        customerFlex: dealsData.some(({ customerFlex }: IDealDataSelector) => customerFlex !== '-'),
        earlyTermination: dealsData.some(({ earlyTermination }: IDealDataSelector) => earlyTermination !== '-'),
        extensions: dealsData.some(({ extensions }: IDealDataSelector) => extensions !== '-'),
        priceSwitch: dealsData.some(({ priceSwitch }: IDealDataSelector) => !priceSwitch?.includes('null')),
        pushVal: dealsData.some(({ pushVal }: IDealDataSelector) => pushVal !== '-'),
        pullVal: dealsData.some(({ pullVal }: IDealDataSelector) => pullVal !== '-'),
        floor: dealsData.some(({ floor }: IDealDataSelector) => floor !== '-'),
      });
    } else {
      setShowOptions({
        cap: true,
        customerFlex: true,
        earlyTermination: true,
        extensions: true,
        priceSwitch: true,
        pushVal: true,
        pullVal: true,
        floor: true,
      });
    }
  }, [dealsData]);

  const handleChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const updateDealEvaluation = [...dealsWithEvaluation];
    const objIndexes = updateDealEvaluation.findIndex((obj: { deal: IDeal }) => obj.deal.id === index.toString());
    const { deal } = updateDealEvaluation[objIndexes];
    const dealTemp = {
      ...deal,
      productName: deal.productName?.map((opt: { name: string }) => {
        const checked = opt.name === event.target.value;
        return {
          ...opt,
          selected: checked,
        };
      }),
    };
    updateDeals(dealTemp);
    setLoading(ELoadingType.NONE);
  };

  const handleAddDealToCompare = () => {
    removeAllDeals();
    const filteredDeals: IDeal[] = availableDeals.filter((deal: IDeal) => compareDealIds.includes(deal.id));
    const dealsWithProductName = filteredDeals.map((deal: IDeal) =>
      updateDealWithAllProduct(fromApi(deal), availableProducts)
    );
    addDealsFromDashboard(dealsWithProductName);
    evaluateDeals();
    // Newly added to handle bookmarked pages by the user
    history.push(`${'/compare'}/${compareDealIds.join()}`);
    setIsModalOpen(false);
  };

  const renderModal = () => {
    if (isModalOpen) {
      return (
        <AddDealToCompareModal
          availableDeals={availableDeals}
          closeModal={() => {
            setIsModalOpen(false);
          }}
          addDealToCompare={handleAddDealToCompare}
          compareDealIds={compareDealIds}
          setCompareDealIds={setCompareDealIds}
        />
      );
    }
    return null;
  };

  const deleteDeal = (index: number) => {
    removeDeal(index);
    compareDealIds.splice(index, 1);
    setCompareDealIds(compareDealIds);
  };

  const renderCollapsableContent = () => {
    return (
      <Collapse in={openCostToserve} timeout="auto" unmountOnExit>
        <tr>
          <SubHeaderData>
            {headers[costToServeYieldHeaderConst].subHeader[0]}
            <WeightHeader>({currencyByWeightLabel})</WeightHeader>
          </SubHeaderData>

          {dealsData.map(({ totalSupplyCost }: IDealDataSelector) => (
            <CostToServeData key={uuid()}>{totalSupplyCost}</CostToServeData>
          ))}
        </tr>
        <tr>
          <SubHeaderData>
            {headers[costToServeYieldHeaderConst].subHeader[1]}
            <WeightHeader>({currencyByWeightLabel})</WeightHeader>
          </SubHeaderData>

          {dealsData.map(({ reimbursedSupplyCost }: IDealDataSelector) => (
            <CostToServeData key={uuid()}>
              {reimbursedSupplyCost?.map((rsc: string) => (
                <p key={uuid()}>{rsc}</p>
              ))}
            </CostToServeData>
          ))}
        </tr>
        <tr>
          <SubHeaderData>
            {headers[costToServeYieldHeaderConst].subHeader[pricingFormulaHeaderConst]}
            <WeightHeader>({currencyByWeightLabel})</WeightHeader>
          </SubHeaderData>

          {dealsData.map(({ variableSupplyCost }: IDealDataSelector) => (
            <CostToServeData key={uuid()}>
              {variableSupplyCost?.map((vsc: React.ReactNode) => (
                <p key={uuid()}>{vsc}</p>
              ))}
            </CostToServeData>
          ))}
        </tr>
        <tr>
          <SubHeaderData>
            {headers[costToServeYieldHeaderConst].subHeader[volumeLabelHeaderConst]} ({'$/yr'})
          </SubHeaderData>

          {dealsData.map(({ fixedSupplyCost }: IDealDataSelector) => (
            <CostToServeData key={uuid()}>
              {fixedSupplyCost?.map((fsc: string) => (
                <p key={uuid()}>{fsc}</p>
              ))}
            </CostToServeData>
          ))}
        </tr>
        <tr>
          <SubHeaderData>
            {headers[costToServeYieldHeaderConst].subHeader[currencyDollarHeaderConst]} ({'KTon'})
          </SubHeaderData>

          {dealsData.map(({ additionalInventory }: IDealDataSelector) => (
            <CostToServeData key={uuid()}>
              {additionalInventory?.map((ai: string) => (
                <p key={uuid()}>{ai}</p>
              ))}
            </CostToServeData>
          ))}
        </tr>
        <tr>
          <SubHeaderData>{headers[costToServeYieldHeaderConst].subHeader[totalRevenueHeaderConst]}</SubHeaderData>

          {dealsData.map(({ additionalTransitDay }: IDealDataSelector) => (
            <CostToServeData key={uuid()}>
              {additionalTransitDay?.map(atd => (
                <p key={uuid()}>{atd}</p>
              ))}
            </CostToServeData>
          ))}
        </tr>
        <tr>
          <SubHeaderData>{headers[costToServeYieldHeaderConst].subHeader[totalNetbackHeaderConst]}</SubHeaderData>

          {dealsData.map(({ modeOfTransport }: IDealDataSelector) => (
            <CostToServeData key={uuid()}>
              {modeOfTransport?.map((mt: React.ReactNode) => (
                <p key={uuid()}>{mt}</p>
              ))}
            </CostToServeData>
          ))}
        </tr>
        <tr>
          <SubHeaderData>{headers[costToServeYieldHeaderConst].subHeader[totalMarginHeaderConst]}</SubHeaderData>

          {dealsData.map(({ incoTerm }: IDealDataSelector) => (
            <CostToServeData key={uuid()}>
              {incoTerm?.map((inc: React.ReactNode) => (
                <p key={uuid()}>{inc}</p>
              ))}
            </CostToServeData>
          ))}
        </tr>
        <tr>
          <SubHeaderData>{headers[costToServeYieldHeaderConst].subHeader[costToServeYieldHeaderConst]}</SubHeaderData>

          {dealsData.map(({ otherSupplyItems }: IDealDataSelector) => (
            <CostToServeBottom key={uuid()}>
              {otherSupplyItems?.map((osi: string) => (
                <p key={uuid()}>{osi}</p>
              ))}
            </CostToServeBottom>
          ))}
        </tr>
      </Collapse>
    );
  };

  const renderCollapseItems = () => {
    return (
      <Collapse in={openRisks} timeout="auto" unmountOnExit>
        {headers[nonstandardRisksHeaderConst] &&
          headers[nonstandardRisksHeaderConst].subHeader.length > 0 &&
          headers[nonstandardRisksHeaderConst].subHeader.map(sbh => {
            return (
              <tr key={uuid()}>
                <SubHeaderData key={uuid()}>{sbh}</SubHeaderData>
                {dealsData.map(({ nonstandardRisks }: IDealDataSelector) => {
                  return (
                    <td key={uuid()}>
                      {nonstandardRisks ? getNonstandardRisksSubHeaderText(nonstandardRisks, sbh) : notSpecified}
                    </td>
                  );
                })}
              </tr>
            );
          })}
      </Collapse>
    );
  };

  const headerNine = 9;
  const headerEleven = 11;
  const headerTwo = 2;

  const renderContent = () => {
    return (
      <tbody>
        <tr>
          <Blanktd />
          {isEditClick &&
            dealsData.map((_deals: IDealDataSelector, index: number) => (
              <RenderDealEditView
                key={`arrow-container-${uuid()}`}
                index={index}
                shiftDealLeft={shiftDealLeft}
                deleteDeal={deleteDeal}
                shiftDealRight={shiftDealRight}
                dealsWithEvaluation={dealsWithEvaluation}
              />
            ))}
        </tr>
        <tr>
          <UnitToggle>
            <UnitToggles />
          </UnitToggle>
          {dealsData.map(({ customer, id, name, products, status }: IDealDataSelector) => {
            return (
              <RenderDealDetails
                key={uuid()}
                customer={customer}
                id={id}
                name={name}
                products={products}
                status={status}
              />
            );
          })}
        </tr>
        <tr>
          <td>{headers[0].header}</td>
          {dealsData.map(({ productName, id }: IDealDataSelector) => (
            <td key={uuid()}>
              {checkProductNameLength(productName) ? (
                <RadioButtonsGroup handleChange={handleChange} options={productName ?? []} id={id} />
              ) : (
                checkProductName(productName)
              )}
            </td>
          ))}
        </tr>
        <tr>
          <td>{headers[1].header}</td>
          {dealsData.map(({ offerTypes }: IDealDataSelector) => (
            <td key={uuid()}>{offerTypes}</td>
          ))}
        </tr>
        <tr>
          <td>{headers[pricingFormulaHeaderConst].header}</td>
          {dealsData.map(({ pricingFormulas }: IDealDataSelector) => (
            <td key={uuid()}>
              {pricingFormulas?.map((pf: string) => (
                <p key={uuid()}>{pf}</p>
              ))}
            </td>
          ))}
        </tr>
        <tr>
          <td>
            {headers[volumeLabelHeaderConst].header}
            <WeightHeader>({volumeLabel})</WeightHeader>
          </td>
          {dealsData.map(({ totalVolume }: IDealDataSelector) => (
            <td key={uuid()}>
              {totalVolume?.map((tv: string) => (
                <p key={uuid()}>{tv}</p>
              ))}
            </td>
          ))}
        </tr>
        <tr>
          <td>
            {headers[currencyDollarHeaderConst].header}
            <WeightHeader>({labels.currency.dollar})</WeightHeader>
          </td>
          {dealsData.map(({ headLine }: IDealDataSelector) => (
            <td key={uuid()}>
              {headLine?.map((hl: string) => (
                <p key={uuid()}>{hl}</p>
              ))}
            </td>
          ))}
        </tr>
        <tr>
          <td>
            {headers[totalRevenueHeaderConst].header}
            <WeightHeader>({currencyByWeightLabel})</WeightHeader>
          </td>

          {dealsData.map(({ totalRevenue }: IDealDataSelector) => (
            <td key={uuid()}>
              {totalRevenue?.map((tr: string) => (
                <p key={uuid()}>{tr}</p>
              ))}
            </td>
          ))}
        </tr>
        <tr>
          <td>
            {headers[totalNetbackHeaderConst].header}
            <WeightHeader>({currencyByWeightLabel})</WeightHeader>
          </td>

          {dealsData.map(({ totalNetback }: IDealDataSelector) => (
            <td key={uuid()}>
              {totalNetback?.map((tn: string) => (
                <p key={uuid()}>{tn}</p>
              ))}
            </td>
          ))}
        </tr>
        <tr>
          <td>
            {headers[totalMarginHeaderConst].header}
            <WeightHeader>({currencyByWeightLabel})</WeightHeader>
          </td>

          {dealsData.map(({ totalMargin }: IDealDataSelector) => (
            <td key={uuid()}>
              {totalMargin?.map((tm: string) => (
                <p key={uuid()}>{tm}</p>
              ))}
            </td>
          ))}
        </tr>
        <tr>
          <td>
            {headers[totalMarginAtRiskHeaderConst].header}
            <WeightHeader>({currencyByWeightLabel})</WeightHeader>
            <ToolTip
              top="5"
              placement="right"
              title="Average Margin ($) - 5 Percentile Margin ($) at Left Tail Distribution"
            />
          </td>

          {dealsData.map(({ totalMarginAtRisk }: IDealDataSelector) => (
            <td key={uuid()}>
              {totalMarginAtRisk?.map((mr: string) => (
                <p key={uuid()}>{mr}</p>
              ))}
            </td>
          ))}
        </tr>
        <tr>
          <td>
            {headers[sharpeRatioHeaderConst].header}
            <ToolTip top="5" placement="right" title="Margin ($)/Margin-at-Risk ($)" />
          </td>

          {dealsData.map(({ sharpeRatio }: IDealDataSelector) => (
            <td key={uuid()}>
              {sharpeRatio?.map((ratio: string) => (
                <p key={uuid()}>{ratio}</p>
              ))}
            </td>
          ))}
        </tr>
        <tr>
          <td>
            {headers[contractBatna1HeaderConst].header}
            <WeightHeader>({currencyByWeightLabel})</WeightHeader>
          </td>

          {dealsData.map(({ contractBatna1 }: IDealDataSelector) => (
            <td key={uuid()}>
              {contractBatna1?.map((cb: string) => (
                <p key={uuid()}>{cb}</p>
              ))}
            </td>
          ))}
        </tr>
        <tr>
          <td>
            {headers[ContractWOOptionsHeaderConst].header}
            <WeightHeader>({currencyByWeightLabel})</WeightHeader>
          </td>

          {dealsData.map(({ contractContractWOOptions }: IDealDataSelector) => (
            <td key={uuid()}>
              {contractContractWOOptions?.map((cc: string | string[]) => (
                <p key={uuid()}>{cc}</p>
              ))}
            </td>
          ))}
        </tr>
        <tr>
          <td>
            {headers[costToServeYieldHeaderConst].header}
            <CostToServeTooltip />
            <IconButtons aria-label="expand row" size="small" onClick={() => setOpenCostToServe(!openCostToserve)}>
              {checkOpenCostToserveValue(openCostToserve)}
            </IconButtons>
          </td>

          {dealsData.map(({ costToServeYield }: IDealDataSelector) => (
            <CostToServeTop key={uuid()}>
              {costToServeYield?.map((csy: string) => (
                <p key={uuid()}>{csy}</p>
              ))}
            </CostToServeTop>
          ))}
        </tr>
        {renderCollapsableContent()}
        <tr>
          <td>
            {headers[creditTermHeaderConst].header}
            <IconButtons aria-label="expand row" size="small" onClick={() => setOpenCreditTerm(!openCreditTerm)}>
              {checkOpenCreditTermValue(openCreditTerm)}
            </IconButtons>
          </td>

          {dealsData.map(({ creditTerm }: IDealDataSelector) => (
            <CreditTermData key={uuid()}>{creditTerm}</CreditTermData>
          ))}
        </tr>
        <Collapse in={openCreditTerm} timeout="auto" unmountOnExit>
          <tr>
            <SubHeaderData>
              {headers[creditTermHeaderConst].subHeader[0]}
              <WeightHeader>({currencyByWeightLabel})</WeightHeader>
            </SubHeaderData>

            {dealsData.map(({ credit }: IDealDataSelector) => (
              <CreditTermData key={uuid()}>
                {credit?.map((cr: string) => (
                  <p key={uuid()}>{cr}</p>
                ))}
              </CreditTermData>
            ))}
          </tr>
          <tr>
            <SubHeaderData>{headers[creditTermHeaderConst].subHeader[1]}</SubHeaderData>

            {dealsData.map(({ probabilityOfDefault }: IDealDataSelector) => (
              <CreditTermData key={uuid()}>{probabilityOfDefault}</CreditTermData>
            ))}
          </tr>
          <tr>
            <SubHeaderData>
              {headers[headerNine].subHeader[headerTwo]}
              <WeightHeader>({labels.currency.dollar})</WeightHeader>
            </SubHeaderData>

            {dealsData.map(({ exposure }: IDealDataSelector) => (
              <CreditTermData key={uuid()}>
                {exposure?.map((exp: string) => (
                  <p key={uuid()}>{exp}</p>
                ))}
              </CreditTermData>
            ))}
          </tr>
          <tr>
            <SubHeaderData>{headers[creditTermHeaderConst].subHeader[volumeLabelHeaderConst]}</SubHeaderData>

            {dealsData.map(({ otherCreditItems }: IDealDataSelector) => (
              <CreditBottom key={uuid()}>{otherCreditItems}</CreditBottom>
            ))}
          </tr>
        </Collapse>
        <tr>
          <td>
            {headers[optionsValueHeaderConst].header} <WeightHeader>({currencyByWeightLabel})</WeightHeader>
            {checkShowOptionsCapCustomerFlexEarlyTerminationAndExtensions(showOptions) &&
            checkShowOptionsFloorPriceSwitchPullValAndPushVal(showOptions) ? (
              <IconDisableButtons aria-label="expand row" size="small">
                <KeyboardArrowDownIcon />
              </IconDisableButtons>
            ) : (
              <IconButtons aria-label="expand row" size="small" onClick={() => setOpenOption(!openOption)}>
                {checkOpenOptionValue(openOption)}
              </IconButtons>
            )}
          </td>

          {dealsData.map(({ optionsValue }: IDealDataSelector) => (
            <td key={uuid()}>
              {optionsValue?.map((opt: string) => (
                <p key={uuid()}>{opt}</p>
              ))}
            </td>
          ))}
        </tr>
        <Collapse in={openOption} timeout="auto" unmountOnExit>
          <RenderShowOptions
            showOptions={showOptions}
            dealsData={dealsData}
            headers={headers}
            currencyByWeightLabel={currencyByWeightLabel}
          />
        </Collapse>

        {headers[nonstandardRisksHeaderConst].subHeader.length > 0 && (
          <tr>
            <td>
              {headers[headerEleven].header}
              <IconButtons aria-label="expand row" size="small" onClick={() => setOpenRisks(!openRisks)}>
                {checkOpenRisksValue(openRisks)}
              </IconButtons>
            </td>
            {dealsData.map(({ nonstandardRisks }: IDealDataSelector) => (
              <td key={uuid()}>{nonstandardRisks ? getNonstandardRisksHeaderText(nonstandardRisks) : notSpecified}</td>
            ))}
          </tr>
        )}
        {renderCollapseItems()}
      </tbody>
    );
  };

  return (
    <>
      <IconWrapper>
        <EditIconWrap data-testid="edit_btn" onClick={() => setIsEditClick(!isEditClick)}>
          <EditText>Edit</EditText>
          <EditIcon />
        </EditIconWrap>
        {dealsWithEvaluation.length < labels.comparisonLimit && (
          <DealWrap data-testid={testIdAddDealButton} onClick={() => setIsModalOpen(true)}>
            <MuiAddCircleOutline />
            <AddDealText>Click here to add a deal</AddDealText>
          </DealWrap>
        )}
        {renderModal()}
      </IconWrapper>
      <ComparisonTable>{renderContent()}</ComparisonTable>
    </>
  );
}
